import { type ComponentProps, forwardRef } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
    PRODUCT_TILE_CUSTOM_CARD_SHELL_CLASS,
    PRODUCT_TILE_CUSTOM_SAND_PLACEHOLDER,
    type ProductTileCustomLayout,
} from './product-tile-custom-types';

export type ProductTileCustomSkeletonProps = ComponentProps<'div'> & { layout?: ProductTileCustomLayout };

/**
 * Loading placeholder that mirrors `ProductTileCustomVerticalCard` and `ProductTileCustomHorizontalCard`
 * (padding, radii, image aspect, and content blocks).
 */
export const ProductTileCustomSkeleton = forwardRef<HTMLDivElement, ProductTileCustomSkeletonProps>(
    function ProductTileCustomSkeleton({ layout = 'vertical', className, ...rest }, ref) {
        if (layout === 'horizontal') {
            return (
                <div
                    ref={ref}
                    data-testid="product-tile-skeleton"
                    className={cn(
                        'product-tile-skeleton w-full min-w-0 max-w-full',
                        'relative min-h-44 flex flex-row items-stretch gap-2 border-0 py-0',
                        PRODUCT_TILE_CUSTOM_CARD_SHELL_CLASS,
                        className
                    )}
                    {...rest}>
                    <div className="relative h-full min-h-32 w-28 min-w-0 max-h-40 shrink-0 self-stretch overflow-hidden rounded-lg bg-transparent">
                        <Skeleton className="size-full min-h-0 rounded-lg" />
                    </div>
                    <div className="relative z-0 flex min-h-0 min-w-0 flex-1 flex-col gap-2 self-stretch">
                        <div className="flex w-full min-w-0 shrink-0 flex-col gap-1">
                            <Skeleton className="h-3.5 w-1/3 max-w-[8rem] rounded-md" />
                            <div className="h-10 w-full min-w-0">
                                <Skeleton className="mb-1 h-3.5 w-full rounded-md" />
                                <Skeleton className="h-3.5 w-[92%] rounded-md" />
                            </div>
                        </div>
                        <div className="flex w-full shrink-0 flex-wrap gap-1">
                            <Skeleton className="h-4 w-12 rounded-sm" />
                            <Skeleton className="h-4 w-10 rounded-sm" />
                        </div>
                        <div className="mt-auto w-full min-w-0 shrink-0">
                            <div className="flex w-full min-w-0 flex-col gap-1">
                                <Skeleton className="h-4 w-20 rounded-md" />
                                <Skeleton className="h-3.5 w-2/3 rounded-md" />
                            </div>
                        </div>
                    </div>
                    <div className="relative z-20 flex w-11 min-w-11 shrink-0 flex-col items-stretch justify-between gap-1 self-stretch">
                        <div className="flex h-10 max-h-10 w-11 min-h-10 shrink-0 items-center justify-center rounded-[40px] p-0">
                            <Skeleton className="size-6 shrink-0 rounded-full" />
                        </div>
                        <div className="flex shrink-0 items-center justify-center pb-0.5">
                            <Skeleton className="size-10 shrink-0 rounded-full" />
                        </div>
                    </div>
                </div>
            );
        }

        if (layout === 'vertical-event') {
            return (
                <div
                    ref={ref}
                    data-testid="product-tile-skeleton"
                    data-tile-variant="vertical-event"
                    className={cn(
                        'product-tile-skeleton h-full w-full min-w-0 max-w-full',
                        'flex flex-col items-start gap-4 rounded-3xl bg-sand-95 border-0 py-0',
                        className
                    )}
                    {...rest}>
                    <div
                        className={cn(
                            'relative z-20 aspect-square w-full min-w-0 max-w-full overflow-hidden rounded-lg',
                            PRODUCT_TILE_CUSTOM_SAND_PLACEHOLDER
                        )}>
                        <Skeleton className="absolute inset-0 size-full min-h-0 rounded-lg" />
                        <div className="pointer-events-none absolute inset-0 z-[5] p-3">
                            <div className="flex w-full min-w-0 items-start justify-end">
                                <Skeleton className="size-6 shrink-0 rounded-full" />
                            </div>
                        </div>
                    </div>

                    <div className="flex w-full min-w-0 flex-col items-start gap-4 pr-2 pb-4">
                        <div className="flex w-full min-w-0 flex-col gap-1">
                            <Skeleton className="h-5 w-2/3 max-w-[12rem] rounded-md" />
                            <div className="h-12 w-full min-w-0">
                                <Skeleton className="mb-1.5 h-3.5 w-full rounded-md" />
                                <Skeleton className="h-3.5 w-4/5 rounded-md" />
                            </div>
                        </div>
                        <div className="flex w-full min-w-0 flex-col gap-1">
                            <Skeleton className="h-6 w-24 rounded-md" />
                            <Skeleton className="h-4 w-2/3 rounded-md" />
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div
                ref={ref}
                data-testid="product-tile-skeleton"
                className={cn(
                    'product-tile-skeleton h-full w-full min-w-0 max-w-full',
                    'flex flex-col items-start gap-4 border-0',
                    PRODUCT_TILE_CUSTOM_CARD_SHELL_CLASS,
                    className
                )}
                {...rest}>
                <div
                    className={cn(
                        'relative z-20 aspect-square w-full min-w-0 max-w-full overflow-hidden rounded-lg',
                        PRODUCT_TILE_CUSTOM_SAND_PLACEHOLDER
                    )}>
                    <Skeleton className="absolute inset-0 size-full min-h-0 rounded-lg" />
                    <div className="pointer-events-none absolute inset-0 z-[5] p-3">
                        <div className="flex w-full min-w-0 items-start justify-between gap-4">
                            <div className="flex min-w-0 flex-1 flex-wrap content-start gap-1">
                                <Skeleton className="h-4 w-12 rounded-sm" />
                                <Skeleton className="h-4 w-10 rounded-sm" />
                            </div>
                            <Skeleton className="size-6 shrink-0 rounded-full" />
                        </div>
                    </div>
                </div>

                <div className="flex w-full min-w-0 flex-col items-start gap-4 pr-0">
                    <div className="flex w-full min-w-0 flex-col gap-1">
                        <Skeleton className="h-5 w-1/2 max-w-[10rem] rounded-md" />
                        <div className="h-12 w-full min-w-0">
                            <Skeleton className="mb-1.5 h-3.5 w-full rounded-md" />
                            <Skeleton className="h-3.5 w-4/5 rounded-md" />
                        </div>
                    </div>
                    <div className="flex w-full min-w-0 flex-col gap-1">
                        <Skeleton className="h-6 w-24 rounded-md" />
                        <Skeleton className="h-4 w-2/3 rounded-md" />
                    </div>
                    <Skeleton className="h-11 w-full max-w-full min-w-0 rounded-[40px] sm:w-[140px]" />
                </div>
            </div>
        );
    }
);

ProductTileCustomSkeleton.displayName = 'ProductTileCustomSkeleton';
